import { FC, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import css from 'classnames'
import { motion } from 'framer-motion'
import { useMediaQuery } from 'usehooks-ts'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { NavSub } from '../nav-sub'
import { MenuModel } from '~/shared/lib'
import { useStore } from '~/app/store'
import { ExpressOrderIndicator } from '~/features/express-order-indicator'
import { useCurrentLegalEntityStores } from '~/shared/lib/hooks/use-current-legal-entity-stores'

interface Props {
  item: MenuModel
  index: number
}

export const NavMenuItem: FC<Props> = observer(({ item, index }) => {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const { warehousesStore } = useStore()
  const stores = useCurrentLegalEntityStores()

  const matches = useMediaQuery('(max-width: 768px)')
  const { pathname } = useLocation()

  const isSubMenu = Boolean(item?.subLinks && item?.subLinks?.length > 0)
  const activeLink = item.subLinks?.some((link) => link.link === pathname)

  useEffect(() => {
    setOpenIndex(null)
  }, [pathname])

  useEffect(() => {
    if (!matches) setOpenIndex(null)
  }, [matches])

  const handleHeadingClick = (e: React.MouseEvent) => {
    if (matches && isSubMenu) {
      e.preventDefault()
      setOpenIndex((prev) => (prev === index ? null : index))
    }
  }

  const renderSubMenu = () => {
    if (!isSubMenu) return null

    if (matches) {
      return (
        <motion.div
          className={css('overflow-hidden', {
            hidden: openIndex !== index,
          })}
          animate={
            openIndex === index ? { height: 'fit-content' } : { height: 0 }
          }
        >
          {item.subLinks?.map((link) => (
            <div key={link.localeTextName} className="pl-5 py-2">
              <Link
                to={link.link}
                className="text-gray-600 flex items-center justify-between gap-3"
              >
                <span>
                  {link.link === '/stores' && stores.length === 1
                    ? t('settings')
                    : t(link.localeTextName)}
                </span>
                {link.badgeKey ? (
                  <span className="relative min-w-[24px] h-[24px] px-2 rounded-full bg-white border border-gray-300 text-[11px] leading-none flex items-center justify-center text-[#242424]">
                    {warehousesStore.getStatusCount(link.badgeKey)}
                    {link.indicatorKey === 'express' ? (
                      <ExpressOrderIndicator
                        variant="dot"
                        className="absolute -right-0.5 -top-0.5"
                      />
                    ) : null}
                  </span>
                ) : null}
              </Link>
            </div>
          ))}
        </motion.div>
      )
    }

    return <NavSub item={item} />
  }

  return (
    <div className="p-0">
      <li
        className={css('relative inline-block group w-full cursor-pointer', {
          '[&>a>div>.icon]:opacity-100 [&>a>div>.icon]:w-full': activeLink,
        })}
      >
        <Link
          to={item.link}
          onClick={(e) => handleHeadingClick(e)}
          className={css(
            'group text-black-700 py-3 md:py-4 px-5 md:px-3 2xl:px-5 flex justify-between items-center',
            { 'hover:bg-slate-100': !matches },
          )}
        >
          <div className="relative flex items-center gap-2 ">
            <span className="icon hidden md:block absolute bottom-[-10px] opacity-0 group-hover:opacity-100 transition left-0 right-0 w-full bg-blue-600 h-[3px] rounded" />
            {t(item.localeTextName)}
            {item.indicatorKey === 'express' ? (
              <ExpressOrderIndicator
                variant="dot"
                className="relative -top-1"
              />
            ) : null}
          </div>
        </Link>

        {renderSubMenu()}
      </li>
    </div>
  )
})
